import {
  MutationCache,
  QueryClient,
  onlineManager,
} from "@tanstack/react-query";
import Toast from "react-native-toast-message";
import i18n from "@/src/i18n";
import { CACHE_MAX_AGE_MS } from "@/src/services/cache/query-persistence";
import { isOfflineError, OfflineError } from "@/src/services/cache/offline";

export { isOfflineError, OfflineError };

/**
 * Offline the app shows what it has cached and accepts no changes: a mutation
 * fails at once (its optimistic update, if any, is rolled back) instead of
 * being queued and replayed later. A mutation that must still run, such as
 * signing out, sets `meta: { offline: "allow" }`.
 */
const mutationCache = new MutationCache({
  onMutate: async (_variables, mutation) => {
    if (mutation.options.meta?.offline === "allow") return;
    if (!onlineManager.isOnline()) {
      Toast.show({ type: "info", text1: i18n.t("LABEL_OFFLINE_READ_ONLY") });
      throw new OfflineError();
    }
  },
});

export const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 1000 * 60,
      // Kept for as long as the persisted cache is valid, so what is stored can
      // be restored and unused entries are not dropped before then.
      gcTime: CACHE_MAX_AGE_MS,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 0,
      // Run (and fail) offline rather than pause; see mutationCache above.
      networkMode: "always",
    },
  },
});
